import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than Inrō?",
    answer:
      "Zapify is better for Instagram-first creators who want flat, predictable pricing, unlimited DMs on paid plans, AI replies with brand voice, Story DM automation, follower and lead attribution, detailed insights, templates, smart offers, and a possible lifetime deal. Inrō may fit larger brands and agencies that want a smart-inbox CRM, payments in DMs, an 8,000+ integration stack, or a done-for-you managed plan, and that are comfortable with active-contact billing.",
  },
  {
    question: "What is the best Inrō alternative?",
    answer:
      "Zapify is a strong Inrō alternative for creators who want Instagram comment-to-DM automation, AI replies, Story DMs, email capture, ask-to-follow flows, detailed Reel/post insights, follower and lead attribution, templates, smart offers, and flat creator pricing with a $0 free plan for 30 days or 1K contacts.",
  },
  {
    question: "Why switch from Inrō to Zapify?",
    answer:
      "Switch to Zapify if you want flat pricing instead of active-contact billing that scales with your reach, unlimited DMs on paid plans, a creator growth dashboard, AI brand-voice replies, and an optional one-time lifetime deal instead of a recurring bill that climbs as you grow.",
  },
  {
    question: "How much does Inrō cost?",
    answer:
      "Inrō prices in EUR. Its public pricing checked June 6, 2026 lists Free at €0 for 100 active contacts/month, Pro from €12.99/month (contact-metered, auto-scaling), Annual Pro at €1,249/year for 10k+ contacts, and a Managed account plan from €200/month. Because Inrō charges by active contacts, your cost rises as more contacts are activated.",
  },
  {
    question: "Does Inrō have unlimited DMs?",
    answer:
      "Inrō supports DMs across all plans, but your usage is capped by an active-contact quota rather than being truly unlimited. The Free plan stops at 100 active contacts/month. Zapify's paid plans are positioned around unlimited DMs and automations instead of a contact cap.",
  },
  {
    question: "Does Zapify have a free plan?",
    answer:
      "Yes. Zapify's free plan is $0 for 30 days or 1K contacts.",
  },
  {
    question: "Does Inrō have a free plan?",
    answer:
      "Yes. Inrō offers a free plan at €0/month with 100 active contacts/month, 3 active automations, 3 campaigns per month, and 50 AI Agent credits.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is positioned as an AI-powered Instagram comment management and automation platform with AI replies and brand voice controls for tone, length, emojis, and hashtags.",
  },
  {
    question: "Does Inrō have AI replies?",
    answer:
      "Yes. Inrō offers an AI Agent that answers, qualifies, and follows up in DMs. The Free plan includes 50 AI Agent credits, and the AI Agent is included on Pro and Annual Pro plans.",
  },
  {
    question: "Does Zapify have a lifetime deal?",
    answer:
      "The supplied Zapify brief lists a limited one-time lifetime access offer at $200 for eligible spots. Verify availability and remaining spots before publishing. Inrō does not publicly offer a lifetime access plan.",
  },
  {
    question: "How does Zapify pricing compare with Inrō?",
    answer:
      "Zapify uses flat plans positioned around unlimited DMs: a $0 free plan, paid plans at $9.99/month and $19.99/month in the brand brief, plus a limited $200 lifetime offer. Inrō uses active-contact pricing in EUR: Free for 100 contacts, Pro from €12.99/month, and Annual Pro at €1,249/year for higher volume. Zapify is more predictable for creators; Inrō's model can cost more during high-reach months.",
  },
  {
    question: "Can I switch from Inrō to Zapify?",
    answer:
      "Yes. Zapify is a good switch if your main workflows are Instagram comment-to-DM, Story DMs, AI replies, lead capture, ask-to-follow, templates, and smart offers. Before switching, check whether you rely on Inrō-specific features like its smart-inbox CRM, payments in DMs, role-based access, or its larger integration stack.",
  },
  {
    question: "Who should still consider Inrō?",
    answer:
      "Inrō may still make sense for larger brands and agencies that want a unified smart-inbox CRM, payments collected in DMs, an 8,000+ integration stack with private API access, role-based access control, or a done-for-you managed account plan, and that prefer active-contact billing.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  inro: string | boolean
  zapifyNote?: string
  inroNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Core Automation",
    rows: [
      { name: "Instagram comment-to-DM automation", zapify: true, inro: true, zapifyNote: "Send DMs from comments with links, offers, and content", inroNote: "Core comment-to-DM feature" },
      { name: "Post and Reel automation", zapify: true, inro: true },
      { name: "Single-post automation", zapify: true, inro: true },
      { name: "Multiple selected posts", zapify: true, inro: false, zapifyNote: "Faster setup across multiple posts", inroNote: "Not clearly specified" },
      { name: "Any-post automation", zapify: true, inro: false, zapifyNote: "Trigger on any eligible post", inroNote: "Not clearly specified" },
      { name: "Story DM automation", zapify: true, inro: true, zapifyNote: "Story-triggered DM workflows", inroNote: "Story mentions and replies triggers" },
      { name: "Keyword triggers", zapify: true, inro: true },
      { name: "Ask-to-follow before sharing content", zapify: true, inro: false, inroNote: "Not publicly confirmed as a native flow" },
      { name: "Email capture in Instagram DMs", zapify: true, inro: true, inroNote: "Collect emails via campaigns/flows" },
    ],
  },
  {
    category: "AI & Intelligence",
    rows: [
      { name: "AI-powered replies", zapify: true, inro: true, zapifyNote: "AI replies with brand voice controls", inroNote: "AI Agent answers, qualifies, follows up" },
      { name: "Brand voice controls", zapify: true, inro: false, zapifyNote: "Tone, length, emojis, hashtags", inroNote: "Exact public parity should be verified" },
      { name: "Templates for quick setup", zapify: true, inro: true, inroNote: "Public template library" },
      { name: "Smart offers / link cards", zapify: true, inro: false, zapifyNote: "Quick offers and clickable DM destinations", inroNote: "Not clearly promoted" },
    ],
  },
  {
    category: "Analytics & Growth",
    rows: [
      { name: "Detailed Reel/post insights", zapify: true, inro: false, zapifyNote: "Reach, views, likes, comments, shares, saves, visits, taps, engagement" },
      { name: "Followers gained attribution", zapify: true, inro: false, zapifyNote: "Zapify insights" },
      { name: "Leads gained attribution", zapify: true, inro: false, zapifyNote: "Zapify insights", inroNote: "Lead capture exists; attribution not confirmed" },
    ],
  },
  {
    category: "Platform & Pricing",
    rows: [
      { name: "Smart inbox / CRM", zapify: "Contact and lead management", inro: true, inroNote: "Stronger unified inbox + CRM" },
      { name: "Payments in DMs", zapify: false, inro: true, zapifyNote: "Not publicly confirmed", inroNote: "Listed on Pro plan" },
      { name: "Multiple Instagram accounts", zapify: true, inro: true, zapifyNote: "Up to 6 on Professional", inroNote: "Plan/workspace dependent" },
      { name: "Team access", zapify: true, inro: true, zapifyNote: "Plan dependent", inroNote: "Up to 10 on Pro, unlimited on Managed" },
      { name: "Language switching", zapify: true, inro: false, zapifyNote: "10+ languages", inroNote: "Not publicly confirmed" },
      { name: "Integrations", zapify: "Core integrations", inro: true, inroNote: "Make, Zapier, webhooks, private API, 8,000+ tools" },
      { name: "Referral / affiliate program", zapify: true, inro: true, zapifyNote: "20% recurring commission", inroNote: "Programs exist; verify terms" },
      { name: "Lifetime access", zapify: "$200 once", inro: "Not available", zapifyNote: "Limited lifetime offer" },
      { name: "Free plan", zapify: "$0 for 30 days or 1K contacts", inro: "€0 for 100 active contacts/month" },
      { name: "Pricing model", zapify: "Flat plans, unlimited DMs", inro: "Active-contact based, auto-scaling" },
      { name: "Currency", zapify: "USD-first creator pricing", inro: "EUR-first" },
      { name: "Meta approval / partner status", zapify: true, inro: true, zapifyNote: "Approved by Meta", inroNote: "Meta Tech Provider, GDPR and EU AI Act compliant" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free plan", zapify: "$0 for 30 days or 1K contacts", inro: "€0 (100 active contacts/month, 3 automations, 50 AI credits)", zapifyWidth: "2%", inroWidth: "2%" },
  { label: "Entry paid plan", zapify: "$9.99/mo (Starter)", inro: "€12.99/mo (Pro, contact-metered)", zapifyWidth: "20%", inroWidth: "26%" },
  { label: "Professional / Annual", zapify: "$19.99/mo (Professional)", inro: "€1,249/yr (Annual Pro, 10k+ contacts)", zapifyWidth: "38%", inroWidth: "100%" },
  { label: "Lifetime access", zapify: "$200 once", inro: "Not available", zapifyWidth: "30%", inroWidth: "0%" },
  { label: "Managed / Done-for-you", zapify: "Not available", inro: "From €200/month", zapifyWidth: "0%", inroWidth: "40%" },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/inro-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Inro vs Zapify: Best Inro Alternative for Instagram DM Automation (2026)",
        description:
          "Compare Inro vs Zapify for Instagram DM automation, AI replies, comment-to-DM, pricing, and creator growth. See why creators pick Zapify's flat pricing and unlimited DMs.",
        url: "https://zapify.pro/comparison/inro-vs-zapify-pro",
        publishedTime: "2026-06-09",
        modifiedTime: "2026-06-09",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "Inro vs Zapify, Zapify vs Inro, Inro alternative, inro.social alternative, Instagram DM automation tool, Instagram comment to DM automation, AI Instagram replies, Story DM automation, Instagram automation for creators" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/inro-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "Inrō vs Zapify", item: "https://zapify.pro/comparison/inro-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "Inrō vs Zapify Pro 2026: Instagram DM Automation Compared",
        description: "Comprehensive comparison of Inrō and Zapify Pro for Instagram DM automation in 2026.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-06-09",
        dateModified: "2026-06-09",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: InroComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function InroComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* ═══ Section 1: Hero ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            Inrō vs{" "}
            <span className="gradient-text">Zapify Pro</span>
            : Instagram DM Automation Compared for 2026
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-2xl mx-auto mb-8 sm:mb-10">
            Inrō and Zapify both turn Instagram comments and DMs into leads and sales. The real difference is how you pay and who the tool is built for. Inrō meters your plan by "active contacts" per month and scales the price up as you grow. Zapify is built for the Instagram-first creator who wants flat, predictable pricing, unlimited DMs, AI replies with brand voice, and an optional one-time lifetime deal.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_Comparison_Hero", "Start Free No Credit Card")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("feature-table")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Full Comparison
            </Button>
          </div>
        </section>

        {/* ═══ Ask AI to Explain ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "Inrō", pageUrl: "https://zapify.pro/comparison/inro-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* ═══ Section 2: Quick Answer Box ═══ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Quick Answer - How does Inrō compare to Zapify?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              <strong className="text-neutral-900 dark:text-white">Choose Zapify if you want flat creator pricing, unlimited DMs, AI replies, growth insights, and a possible lifetime deal.</strong>{" "}
              Choose Inrō if you are a larger brand or agency that wants contact-metered billing, a done-for-you managed plan, or its smart-inbox CRM and 8,000+ integration setup. Zapify adds{" "}
              <strong className="bg-clip-text text-transparent bg-gradient-to-r from-brand-accent to-brand-accent">
                flat, predictable pricing, unlimited DMs and automations on paid plans, AI replies with brand voice, detailed Reel and post insights, follower and lead growth tracking, and an optional one-time lifetime deal
              </strong>
              — instead of a bill that climbs with every viral post.
            </p>
          </article>
        </section>

        {/* ═══ Section 3: Feature Comparison Table ═══ */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to Inrō feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">
                      Feature
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">
                      Zapify
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">
                      Inrō
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">
                        {cat.category}
                      </td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.inro === "boolean" ? (row.inro ? "Yes" : "No") : row.inro}</span>
                            {row.inroNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.inroNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ═══ Section 4: Pricing Breakdown ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Inrō's contact-metered pricing vs{" "}
              <span className="gradient-text">Zapify's flat pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Inrō charges by contacts in EUR. Zapify keeps it flat in USD with a lifetime access option.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">Inrō</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.inro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ═══ Lifetime Access Pricing ═══ */}
        <LifetimeAccessPricing
          spotsLeft={87}
          totalSpots={100}
          price={200}
          originalMonthlyPrice={20}
          onGetAccess={() => handleCTA("CTA_Inro_Lifetime_Pricing", "Claim Lifetime Access")}
        />

        {/* ═══ Section 5: Flat Pricing vs Contact-Metered ═══ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Flat Pricing vs{" "}
              <span className="gradient-text">Contact-Metered Billing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Inrō charges by active contacts and auto-scales. That means your cost is tied to how many people your content reaches. Zapify's paid plans are positioned around unlimited DMs and flat pricing, so growth does not punish you with a bigger invoice.
            </Paragraph>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Zapify Flat */}
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-600 dark:text-green-400">Zapify - Flat Pricing</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                You grow from 500 to 50,000 contacts:
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300 mb-4">
                <Check className="inline w-4 h-4 mr-1" /> Still $19.99/mo — unlimited DMs, no overage fees
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Want to stop paying monthly?
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3 text-sm text-green-700 dark:text-green-300">
                <Check className="inline w-4 h-4 mr-1" /> $200 lifetime — never pay again
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Predictable costs. No surprises as you scale.
              </p>
            </div>

            {/* Inrō Metered */}
            <div className="rounded-2xl border-2 border-red-200 dark:border-red-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-red-500 dark:text-red-400">Inrō - Contact-Metered</h3>
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                You grow from 500 to 50,000 contacts:
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-4">
                <X className="inline w-4 h-4 mr-1" /> Price jumps from €12.99/mo to €1,249/year tier
              </div>

              <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-3">
                Want lifetime access?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">
                <X className="inline w-4 h-4 mr-1" /> Not available — recurring billing only
              </div>

              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-4">
                Costs rise with your audience. Enterprise-focused pricing model.
              </p>
            </div>
          </div>
        </section>

        {/* ═══ Section 6: FAQ ═══ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about Inrō vs Zapify
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "🔍", "🔄", "💸", "📩", "🆓", "🆓", "🤖", "🤖", "💎", "💰", "🔄", "🏢"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ═══ Section 7: Bottom CTA ═══ */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          {/* Gradient blob */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Ready to grow without <span className="gradient-text">rising costs?</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Flat pricing, unlimited DMs, and AI-powered growth. Try Zapify free and keep your costs predictable.
            </p>

            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs LinktoDM", href: "/comparison/linktodm-vs-zapify-pro" },
              { label: "Zapify vs CreatorFlow", href: "/comparison/creatorflow-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            Inrō is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by Inrō. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Inrō prices are listed in EUR on its public pricing page. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
